import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Banknote,
  CalendarRange,
  ChevronLeft,
  CircleDollarSign,
  LoaderCircle,
  LogOut,
  Plus,
  RefreshCw,
  Search,
  Share2,
  Wallet,
} from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import { useLeadAttendance } from '../../context/LeadAttendanceContext';

interface GroupFinanceScreenProps {
  groupName: string;
  onBack: () => void;
  onLogout: () => void;
}

interface FinanceAnalytics {
  groupId: string;
  totalCampaigns: number;
  totalRaised: number;
  statusCounts?: {
    active: number;
    inactive: number;
  };
  topCampaigns?: Array<{
    id: string;
    name: string;
    count: number;
    totalAmount: number;
  }>;
}

interface FinanceCheckout {
  id: string;
  title: string;
  description?: string;
  amount?: number;
  totalAmount?: number;
  createdAt?: string;
  startsOn?: string;
  endsOn?: string;
  sharableUrl?: string;
  sharebleUrl?: string;
  paymentMethods?: Array<string | { id?: string; name?: string; title?: string }>;
}

interface FinancePayment {
  id: string;
  amount?: number;
  status?: string;
  provider?: string;
  createdAt?: string;
  confirmedAt?: string;
  customerName?: string;
  customerEmail?: string;
  email?: string;
}

interface FinanceSummary {
  total?: number;
  availableTotal?: number;
  pendingTotal?: number;
  totalPayments?: number;
  byStatus?: Record<string, { count: number; total: number }>;
  byProvider?: Record<string, { count: number; total: number }>;
}

interface PaginatedResponse<T> {
  docs?: T[];
  totalDocs?: number;
  hasNextPage?: boolean;
}

export default function GroupFinanceScreen({
  groupName,
  onBack,
  onLogout,
}: GroupFinanceScreenProps) {
  const { t } = useTranslation();
  const { apiUrl, authenticatedFetch, selectedGroupId } = useLeadAttendance();
  const [analytics, setAnalytics] = useState<FinanceAnalytics | null>(null);
  const [checkouts, setCheckouts] = useState<FinanceCheckout[]>([]);
  const [checkoutsTotal, setCheckoutsTotal] = useState(0);
  const [selectedCheckoutId, setSelectedCheckoutId] = useState<string | null>(null);
  const [selectedCheckout, setSelectedCheckout] = useState<FinanceCheckout | null>(null);
  const [checkoutSummary, setCheckoutSummary] = useState<FinanceSummary | null>(null);
  const [payments, setPayments] = useState<FinancePayment[]>([]);
  const [screenError, setScreenError] = useState<string | null>(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(true);
  const [isLoadingCheckouts, setIsLoadingCheckouts] = useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingMoreCheckouts, setIsLoadingMoreCheckouts] = useState(false);
  const [isLoadingMorePayments, setIsLoadingMorePayments] = useState(false);
  const [hasMoreCheckouts, setHasMoreCheckouts] = useState(false);
  const [hasMorePayments, setHasMorePayments] = useState(false);
  const [checkoutsPage, setCheckoutsPage] = useState(0);
  const [paymentsPage, setPaymentsPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [startsFrom, setStartsFrom] = useState('');
  const [endsTo, setEndsTo] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [shareMessage, setShareMessage] = useState<string | null>(null);
  const [createTitle, setCreateTitle] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createAmount, setCreateAmount] = useState('0');
  const [createStartsOn, setCreateStartsOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [createEndsOn, setCreateEndsOn] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().slice(0, 10);
  });
  const [createFreeValue, setCreateFreeValue] = useState(false);
  const checkoutsRef = useRef<FinanceCheckout[]>([]);
  const paymentsRef = useRef<FinancePayment[]>([]);

  useEffect(() => {
    checkoutsRef.current = checkouts;
  }, [checkouts]);

  useEffect(() => {
    paymentsRef.current = payments;
  }, [payments]);

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 2,
      }),
    []
  );

  const formatCurrency = useCallback(
    (value?: number | null) => currencyFormatter.format(Number(value || 0)),
    [currencyFormatter]
  );

  const formatDate = useCallback((value?: string) => {
    if (!value) {
      return '---';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '---';
    }

    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }, []);

  const getCheckoutStatus = useCallback(
    (checkout: FinanceCheckout) => {
      const now = Date.now();
      const startsOn = checkout.startsOn ? new Date(checkout.startsOn).getTime() : null;
      const endsOn = checkout.endsOn ? new Date(checkout.endsOn).getTime() : null;

      if (startsOn && startsOn > now) {
        return {
          label: t('financeScheduledLabel', { defaultValue: 'Programado' }),
          className: 'border-amber-200 bg-amber-50 text-amber-700',
        };
      }

      if (endsOn && endsOn < now) {
        return {
          label: t('financeInactiveLabel', { defaultValue: 'Encerrado' }),
          className: 'border-slate-200 bg-slate-100 text-slate-600',
        };
      }

      return {
        label: t('financeActiveLabel', { defaultValue: 'Ativo' }),
        className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      };
    },
    [t]
  );

  const getCampaignPublicUrl = useCallback(
    (campaign?: FinanceCheckout | null) => {
      return campaign?.sharebleUrl || campaign?.sharableUrl || '';
    },
    []
  );

  const handleShareCampaign = useCallback(async () => {
    const url = getCampaignPublicUrl(selectedCheckout);

    if (!url || !selectedCheckout) {
      return;
    }

    try {
      if (navigator.share) {
        await navigator.share({
          title: selectedCheckout.title,
          text: selectedCheckout.description || selectedCheckout.title,
          url,
        });
        setShareMessage(t('financeShareSuccess', { defaultValue: 'Link da campanha pronto para partilha.' }));
        return;
      }

      await navigator.clipboard.writeText(url);
      setShareMessage(t('financeCopySuccess', { defaultValue: 'Link da campanha copiado.' }));
    } catch (_error) {
      setShareMessage(t('financeShareError', { defaultValue: 'Não foi possível partilhar a campanha.' }));
    }
  }, [getCampaignPublicUrl, selectedCheckout, t]);

  const buildDistributionRows = useCallback(
    (source?: Record<string, { count: number; total: number }>) => {
      const entries = Object.entries(source || {});

      if (entries.length === 0) {
        return [] as Array<{
          key: string;
          count: number;
          total: number;
          percent: number;
        }>;
      }

      const maxAmount = entries.reduce((acc, [, value]) => {
        return Math.max(acc, Number(value?.total || 0));
      }, 0);

      return entries
        .map(([key, value]) => {
          const total = Number(value?.total || 0);
          const count = Number(value?.count || 0);
          const percent = maxAmount > 0 ? Math.max(6, (total / maxAmount) * 100) : 0;

          return {
            key,
            count,
            total,
            percent,
          };
        })
        .sort((a, b) => b.total - a.total);
    },
    []
  );

  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();
    params.set('limit', '20');

    if (search.trim()) {
      params.set('search', search.trim());
    }

    if (status !== 'all') {
      params.set('status', status);
    }

    if (startsFrom) {
      params.set('startsFrom', new Date(`${startsFrom}T00:00:00`).toISOString());
    }

    if (endsTo) {
      params.set('endsTo', new Date(`${endsTo}T23:59:59`).toISOString());
    }

    return params.toString();
  }, [endsTo, search, startsFrom, status]);

  const buildPaymentsQueryString = useCallback((page: number) => {
    const params = new URLSearchParams();
    params.set('limit', '10');
    params.set('page', String(page));
    return params.toString();
  }, []);

  const loadAnalytics = useCallback(async () => {
    if (!selectedGroupId) {
      return;
    }

    setIsLoadingAnalytics(true);

    try {
      const response = await authenticatedFetch(`${apiUrl}/chatbot/groups/${selectedGroupId}/finance/analytics`);
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || 'Failed to load finance analytics.');
      }

      setAnalytics(data as FinanceAnalytics);
    } finally {
      setIsLoadingAnalytics(false);
    }
  }, [apiUrl, authenticatedFetch, selectedGroupId]);

  const loadCheckouts = useCallback(async (options?: { append?: boolean; page?: number }) => {
    if (!selectedGroupId) {
      return;
    }

    const append = Boolean(options?.append);
    const page = options?.page ?? 0;

    if (append) {
      setIsLoadingMoreCheckouts(true);
    } else {
      setIsLoadingCheckouts(true);
    }

    try {
      const baseQuery = buildQueryString();
      const queryString = `${baseQuery}${baseQuery ? '&' : ''}page=${page}`;
      const url = `${apiUrl}/chatbot/groups/${selectedGroupId}/finance/checkouts${queryString ? `?${queryString}` : ''}`;
      const response = await authenticatedFetch(url);
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || 'Failed to load checkouts.');
      }

      const nextCheckouts = Array.isArray((data as PaginatedResponse<FinanceCheckout>).docs)
        ? ((data as PaginatedResponse<FinanceCheckout>).docs as FinanceCheckout[])
        : [];

      const mergedCheckouts = append
        ? [...checkoutsRef.current, ...nextCheckouts]
        : nextCheckouts;
      const total = Number((data as PaginatedResponse<FinanceCheckout>).totalDocs || mergedCheckouts.length);
      const explicitHasNextPage = Boolean((data as PaginatedResponse<FinanceCheckout>).hasNextPage);
      const computedHasMore = mergedCheckouts.length < total;

      checkoutsRef.current = mergedCheckouts;
      setCheckouts(mergedCheckouts);
      setCheckoutsPage(page);
      setCheckoutsTotal(total);
      setHasMoreCheckouts(explicitHasNextPage || computedHasMore);
      setSelectedCheckoutId((current) => {
        if (current && mergedCheckouts.some((checkout) => checkout.id === current)) {
          return current;
        }

        return mergedCheckouts[0]?.id || null;
      });
    } finally {
      setIsLoadingCheckouts(false);
      setIsLoadingMoreCheckouts(false);
    }
  }, [apiUrl, authenticatedFetch, buildQueryString, selectedGroupId]);

  const loadCheckoutPayments = useCallback(
    async (checkoutId: string, page: number, append = false) => {
      if (!selectedGroupId) {
        return;
      }

      if (append) {
        setIsLoadingMorePayments(true);
      }

      try {
        const paymentsQuery = buildPaymentsQueryString(page);
        const paymentsResponse = await authenticatedFetch(
          `${apiUrl}/chatbot/groups/${selectedGroupId}/finance/checkouts/${checkoutId}/payments?${paymentsQuery}`
        );
        const paymentsData = await paymentsResponse.json().catch(() => ({}));

        if (!paymentsResponse.ok) {
          throw new Error(paymentsData.message || 'Failed to load checkout payments.');
        }

        const nextPayments = Array.isArray((paymentsData as PaginatedResponse<FinancePayment>).docs)
          ? ((paymentsData as PaginatedResponse<FinancePayment>).docs as FinancePayment[])
          : [];
        const mergedPayments = append
          ? [...paymentsRef.current, ...nextPayments]
          : nextPayments;
        const totalPaymentsCount = Number((paymentsData as PaginatedResponse<FinancePayment>).totalDocs || mergedPayments.length);
        const explicitHasNextPage = Boolean((paymentsData as PaginatedResponse<FinancePayment>).hasNextPage);

        paymentsRef.current = mergedPayments;
        setPayments(mergedPayments);
        setPaymentsPage(page);
        setHasMorePayments(explicitHasNextPage || mergedPayments.length < totalPaymentsCount);
      } finally {
        setIsLoadingMorePayments(false);
      }
    },
    [apiUrl, authenticatedFetch, buildPaymentsQueryString, selectedGroupId]
  );

  const loadCheckoutDetail = useCallback(
    async (checkoutId: string) => {
      if (!selectedGroupId) {
        return;
      }

      setIsLoadingDetail(true);

      try {
        const [detailResponse, summaryResponse] = await Promise.all([
          authenticatedFetch(`${apiUrl}/chatbot/groups/${selectedGroupId}/finance/checkouts/${checkoutId}`),
          authenticatedFetch(`${apiUrl}/chatbot/groups/${selectedGroupId}/finance/checkouts/${checkoutId}/summary`),
        ]);

        const [detailData, summaryData] = await Promise.all([
          detailResponse.json().catch(() => ({})),
          summaryResponse.json().catch(() => ({})),
        ]);

        if (!detailResponse.ok) {
          throw new Error(detailData.message || 'Failed to load checkout detail.');
        }

        if (!summaryResponse.ok) {
          throw new Error(summaryData.message || 'Failed to load checkout summary.');
        }

        setSelectedCheckout(detailData as FinanceCheckout);
        setCheckoutSummary(summaryData as FinanceSummary);
        await loadCheckoutPayments(checkoutId, 0, false);
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [apiUrl, authenticatedFetch, loadCheckoutPayments, selectedGroupId]
  );

  const handleLoadMoreCheckouts = useCallback(() => {
    if (!hasMoreCheckouts || isLoadingMoreCheckouts || isLoadingCheckouts) {
      return;
    }

    void loadCheckouts({ append: true, page: checkoutsPage + 1 });
  }, [checkoutsPage, hasMoreCheckouts, isLoadingCheckouts, isLoadingMoreCheckouts, loadCheckouts]);

  const handleLoadMorePayments = useCallback(() => {
    if (!selectedCheckoutId || !hasMorePayments || isLoadingMorePayments || isLoadingDetail) {
      return;
    }

    void loadCheckoutPayments(selectedCheckoutId, paymentsPage + 1, true);
  }, [hasMorePayments, isLoadingDetail, isLoadingMorePayments, loadCheckoutPayments, paymentsPage, selectedCheckoutId]);

  const refreshFinance = useCallback(async () => {
    if (!selectedGroupId) {
      return;
    }

    setScreenError(null);

    try {
      await Promise.all([loadAnalytics(), loadCheckouts({ append: false, page: 0 })]);
    } catch (error) {
      setScreenError(error instanceof Error ? error.message : 'Failed to load finance data.');
    }
  }, [loadAnalytics, loadCheckouts, selectedGroupId]);

  const handleCreateCheckout = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!selectedGroupId) {
        return;
      }

      setIsCreating(true);
      setScreenError(null);

      try {
        const response = await authenticatedFetch(`${apiUrl}/chatbot/groups/${selectedGroupId}/finance/checkouts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: createTitle.trim(),
            description: createDescription.trim(),
            amount: Number(createAmount || 0),
            freeValue: createFreeValue,
            allowAnonymous: true,
            startsOn: new Date(`${createStartsOn}T00:00:00`).toISOString(),
            endsOn: new Date(`${createEndsOn}T23:59:59`).toISOString(),
          }),
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.message || 'Failed to create checkout.');
        }

        setShowCreateForm(false);
        setCreateTitle('');
        setCreateDescription('');
        setCreateAmount('0');
        setCreateFreeValue(false);
        await Promise.all([loadAnalytics(), loadCheckouts({ append: false, page: 0 })]);

        if (data?.id) {
          setSelectedCheckoutId(String(data.id));
        }
      } catch (error) {
        setScreenError(error instanceof Error ? error.message : 'Failed to create checkout.');
      } finally {
        setIsCreating(false);
      }
    },
    [
      apiUrl,
      authenticatedFetch,
      createAmount,
      createDescription,
      createEndsOn,
      createFreeValue,
      createStartsOn,
      createTitle,
      loadAnalytics,
      loadCheckouts,
      selectedGroupId,
    ]
  );

  useEffect(() => {
    void refreshFinance();
  }, [refreshFinance]);

  useEffect(() => {
    if (!selectedCheckoutId) {
      setSelectedCheckout(null);
      setCheckoutSummary(null);
      paymentsRef.current = [];
      setPayments([]);
      setShareMessage(null);
      return;
    }

    void (async () => {
      try {
        setScreenError(null);
        await loadCheckoutDetail(selectedCheckoutId);
      } catch (error) {
        setScreenError(error instanceof Error ? error.message : 'Failed to load checkout detail.');
      }
    })();
  }, [loadCheckoutDetail, selectedCheckoutId]);

  const summaryCards = [
    {
      icon: Banknote,
      label: t('financeSummaryCheckouts'),
      value: isLoadingAnalytics ? '...' : String(analytics?.totalCampaigns ?? checkoutsTotal),
      detail: t('financeSummaryCheckoutsDetail', {
        defaultValue: '{{active}} ativos / {{inactive}} encerrados',
        active: analytics?.statusCounts?.active ?? 0,
        inactive: analytics?.statusCounts?.inactive ?? 0,
      }),
    },
    {
      icon: CircleDollarSign,
      label: t('financeSummaryRaised'),
      value: isLoadingAnalytics ? '...' : formatCurrency(analytics?.totalRaised),
      detail: t('financeSummaryRaisedDetail', {
        defaultValue: 'Somatório confirmado do grupo',
      }),
    },
    {
      icon: Wallet,
      label: t('financeSummaryStatus'),
      value: selectedCheckout ? getCheckoutStatus(selectedCheckout).label : t('financeNoSelectionShort', { defaultValue: 'Sem seleção' }),
      detail: selectedCheckout
        ? t('financeSummaryStatusDetail', {
            defaultValue: 'Campanha atual: {{title}}',
            title: selectedCheckout.title,
          })
        : t('financeSummaryStatusIdle', { defaultValue: 'Selecione uma campanha para ver o saldo.' }),
    },
  ];

  const statusRows = useMemo(
    () => buildDistributionRows(checkoutSummary?.byStatus),
    [buildDistributionRows, checkoutSummary?.byStatus]
  );

  const providerRows = useMemo(
    () => buildDistributionRows(checkoutSummary?.byProvider),
    [buildDistributionRows, checkoutSummary?.byProvider]
  );

  const campaignPublicUrl = useMemo(
    () => getCampaignPublicUrl(selectedCheckout),
    [getCampaignPublicUrl, selectedCheckout]
  );

  return (
    <section className="min-h-[100dvh] bg-slate-50 px-2 py-3 sm:px-3 sm:py-4">
      <div className="mx-auto flex w-full max-w-none flex-col gap-4">
        <header className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              title={t('backToCeremonies')}
              aria-label={t('backToCeremonies')}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div>
              <p className="text-sm font-medium text-slate-500">{t('financeScreenEyebrow')}</p>
              <h1 className="text-lg font-bold text-slate-900">{groupName}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void refreshFinance()}
              className="inline-flex h-10 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 shadow-sm transition hover:border-emerald-200 hover:text-emerald-700"
            >
              <RefreshCw className="h-4 w-4" />
              <span className="hidden sm:inline">{t('refresh')}</span>
            </button>
            <LanguageSwitcher variant="light" />
            <button
              onClick={onLogout}
              title={t('logout')}
              aria-label={t('logout')}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:text-red-600"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </header>

        {screenError ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{screenError}</div>
        ) : null}

        <div className="grid gap-3 lg:grid-cols-3">
          {summaryCards.map((card) => {
            const Icon = card.icon;

            return (
              <div key={card.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-medium text-slate-500">{card.label}</p>
                    <p className="mt-1 text-lg font-bold text-slate-900">{card.value}</p>
                  </div>
                  <div className="rounded-2xl bg-emerald-50 p-2 text-emerald-700">
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
                <p className="mt-3 text-xs text-slate-500">{card.detail}</p>
              </div>
            );
          })}
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">{t('financeModuleTitle', { defaultValue: 'Campanhas do grupo' })}</p>
              <p className="text-sm text-slate-500">
                {t('financeModuleSubtitle', {
                  defaultValue: 'Pesquise, filtre e acompanhe pagamentos e saldo de cada campanha.',
                })}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowCreateForm((current) => !current)}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
            >
              <Plus className="h-4 w-4" />
              {t('financeCreateCheckout', { defaultValue: 'Nova campanha' })}
            </button>
          </div>

          {showCreateForm ? (
            <form onSubmit={handleCreateCheckout} className="mt-4 grid gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 lg:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm text-slate-600 lg:col-span-2">
                <span>{t('ceremonyTitle', { defaultValue: 'Título' })}</span>
                <input
                  value={createTitle}
                  onChange={(event) => setCreateTitle(event.target.value)}
                  required
                  className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-emerald-300"
                  placeholder={t('financeCreateTitlePlaceholder', { defaultValue: 'Ex: Oferta do mês, Retiro, Ação solidária' })}
                />
              </label>

              <label className="flex flex-col gap-1 text-sm text-slate-600 lg:col-span-2">
                <span>{t('generalAnnotation', { defaultValue: 'Descrição' })}</span>
                <textarea
                  value={createDescription}
                  onChange={(event) => setCreateDescription(event.target.value)}
                  rows={3}
                  className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-emerald-300"
                  placeholder={t('financeCreateDescriptionPlaceholder', { defaultValue: 'Explique o objetivo da arrecadação.' })}
                />
              </label>

              <label className="flex flex-col gap-1 text-sm text-slate-600">
                <span>{t('financeAmountLabel', { defaultValue: 'Valor sugerido' })}</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={createAmount}
                  onChange={(event) => setCreateAmount(event.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-emerald-300"
                />
              </label>

              <label className="flex flex-col gap-1 text-sm text-slate-600">
                <span>{t('financeFreeValueLabel', { defaultValue: 'Doação de valor livre' })}</span>
                <select
                  value={createFreeValue ? 'yes' : 'no'}
                  onChange={(event) => setCreateFreeValue(event.target.value === 'yes')}
                  className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-emerald-300"
                >
                  <option value="no">{t('financeNoOption', { defaultValue: 'Não' })}</option>
                  <option value="yes">{t('financeYesOption', { defaultValue: 'Sim' })}</option>
                </select>
              </label>

              <label className="flex flex-col gap-1 text-sm text-slate-600">
                <span>{t('financeStartsOnLabel', { defaultValue: 'Início' })}</span>
                <input
                  type="date"
                  value={createStartsOn}
                  onChange={(event) => setCreateStartsOn(event.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-emerald-300"
                />
              </label>

              <label className="flex flex-col gap-1 text-sm text-slate-600">
                <span>{t('financeEndsOnLabel', { defaultValue: 'Fim' })}</span>
                <input
                  type="date"
                  value={createEndsOn}
                  onChange={(event) => setCreateEndsOn(event.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-emerald-300"
                />
              </label>

              <div className="flex items-center gap-2 lg:col-span-2">
                <button
                  type="submit"
                  disabled={isCreating || !createTitle.trim()}
                  className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isCreating ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  {t('create', { defaultValue: 'Criar' })}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
                >
                  {t('cancel', { defaultValue: 'Cancelar' })}
                </button>
              </div>
            </form>
          ) : null}

          <div className="mt-4 grid gap-3 xl:grid-cols-[360px_minmax(0,1fr)]">
            <aside className="rounded-3xl border border-slate-200 bg-slate-50 p-3">
              <div className="grid gap-3">
                <label className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-slate-500 shadow-sm">
                  <Search className="h-4 w-4" />
                  <input
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        setSearch(searchInput);
                      }
                    }}
                    placeholder={t('financeSearchPlaceholder', { defaultValue: 'Buscar campanha...' })}
                    className="w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
                  />
                </label>

                <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-1">
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
                    <span>{t('filterStatus')}</span>
                    <select
                      value={status}
                      onChange={(event) => setStatus(event.target.value as 'all' | 'active' | 'inactive')}
                      className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                    >
                      <option value="all">{t('allStatuses')}</option>
                      <option value="active">{t('financeActiveLabel', { defaultValue: 'Ativo' })}</option>
                      <option value="inactive">{t('financeInactiveLabel', { defaultValue: 'Encerrado' })}</option>
                    </select>
                  </label>

                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
                    <span>{t('fromLabel')}</span>
                    <input
                      type="date"
                      value={startsFrom}
                      onChange={(event) => setStartsFrom(event.target.value)}
                      className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                    />
                  </label>

                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
                    <span>{t('toLabel')}</span>
                    <input
                      type="date"
                      value={endsTo}
                      onChange={(event) => setEndsTo(event.target.value)}
                      className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                    />
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSearch(searchInput)}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    <Search className="h-4 w-4" />
                    {t('financeSearchButton', { defaultValue: 'Buscar campanha' })}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput('');
                      setSearch('');
                      setStatus('all');
                      setStartsFrom('');
                      setEndsTo('');
                    }}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
                  >
                    {t('clearChat', { defaultValue: 'Limpar' })}
                  </button>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between text-xs font-medium uppercase tracking-[0.18em] text-slate-400">
                <span>{t('financeListTitle', { defaultValue: 'Campanhas' })}</span>
                <span>{checkoutsTotal}</span>
              </div>

              <div className="mt-3 flex max-h-[52vh] flex-col gap-3 overflow-y-auto pr-1">
                {isLoadingCheckouts ? (
                  <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-4 text-sm text-slate-500">
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                    {t('loading')}
                  </div>
                ) : null}

                {!isLoadingCheckouts && checkouts.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500">
                    {t('financeEmptyState', { defaultValue: 'Nenhuma campanha encontrada com os filtros atuais.' })}
                  </div>
                ) : null}

                {checkouts.map((checkout) => {
                  const statusMeta = getCheckoutStatus(checkout);
                  const isSelected = checkout.id === selectedCheckoutId;

                  return (
                    <button
                      key={checkout.id}
                      type="button"
                      onClick={() => setSelectedCheckoutId(checkout.id)}
                      className={`rounded-2xl border px-4 py-3 text-left shadow-sm transition ${
                        isSelected
                          ? 'border-emerald-300 bg-emerald-50/80'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{checkout.title}</p>
                          <p className="mt-1 line-clamp-2 text-xs text-slate-500">
                            {checkout.description || t('financeNoDescription', { defaultValue: 'Sem descrição.' })}
                          </p>
                        </div>
                        <span className={`inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold ${statusMeta.className}`}>
                          {statusMeta.label}
                        </span>
                      </div>
                      <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                        <span>{formatCurrency(checkout.totalAmount ?? checkout.amount)}</span>
                        <span>{formatDate(checkout.endsOn || checkout.createdAt)}</span>
                      </div>
                    </button>
                  );
                })}

                {hasMoreCheckouts ? (
                  <button
                    type="button"
                    onClick={handleLoadMoreCheckouts}
                    disabled={isLoadingMoreCheckouts}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isLoadingMoreCheckouts ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                    {isLoadingMoreCheckouts ? t('loadingMore') : t('financeLoadMoreCheckouts', { defaultValue: 'Carregar mais campanhas' })}
                  </button>
                ) : null}
              </div>
            </aside>

            <div className="grid gap-4">
              <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                {isLoadingDetail ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                    {t('financeLoadingDetail', { defaultValue: 'Carregando campanha...' })}
                  </div>
                ) : null}

                {!isLoadingDetail && !selectedCheckout ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                    {t('financeNoSelection', { defaultValue: 'Selecione uma campanha para ver pagamentos, saldo e detalhes.' })}
                  </div>
                ) : null}

                {!isLoadingDetail && selectedCheckout ? (
                  <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
                    <div>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h2 className="text-xl font-bold text-slate-900">{selectedCheckout.title}</h2>
                            <span className={`inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold ${getCheckoutStatus(selectedCheckout).className}`}>
                              {getCheckoutStatus(selectedCheckout).label}
                            </span>
                          </div>
                          <p className="mt-2 max-w-2xl text-sm text-slate-600">
                            {selectedCheckout.description || t('financeNoDescription', { defaultValue: 'Sem descrição.' })}
                          </p>
                        </div>
                        <div className="rounded-2xl bg-slate-50 px-4 py-3 text-right">
                          <p className="text-xs font-medium uppercase tracking-[0.16em] text-slate-400">{t('financeTargetAmount', { defaultValue: 'Valor base' })}</p>
                          <p className="mt-1 text-lg font-bold text-slate-900">{formatCurrency(selectedCheckout.amount)}</p>
                        </div>
                      </div>

                      <div className="mt-4 grid gap-3 md:grid-cols-2">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                          <p className="text-xs font-medium text-slate-500">{t('dateLabel')}</p>
                          <p className="mt-1 text-sm font-semibold text-slate-900">{formatDate(selectedCheckout.startsOn)}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                          <p className="text-xs font-medium text-slate-500">{t('financeEndsOnLabel', { defaultValue: 'Fim' })}</p>
                          <p className="mt-1 text-sm font-semibold text-slate-900">{formatDate(selectedCheckout.endsOn)}</p>
                        </div>
                      </div>

                      {campaignPublicUrl ? (
                        <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                              <p className="font-semibold">{t('financeCheckoutLink', { defaultValue: 'Link público da campanha' })}</p>
                              <a
                                href={campaignPublicUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-1 block break-all text-emerald-700 underline"
                              >
                                {campaignPublicUrl}
                              </a>
                            </div>
                            <button
                              type="button"
                              onClick={() => void handleShareCampaign()}
                              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:border-emerald-300 hover:text-emerald-800"
                            >
                              <Share2 className="h-4 w-4" />
                              {t('financeShareCampaign', { defaultValue: 'Partilhar campanha' })}
                            </button>
                          </div>
                          {shareMessage ? <p className="mt-3 text-xs font-medium text-emerald-700">{shareMessage}</p> : null}
                        </div>
                      ) : null}

                      <div className="mt-4">
                        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                          <CalendarRange className="h-4 w-4 text-slate-500" />
                          {t('financeRecentPayments', { defaultValue: 'Pagamentos recentes' })}
                        </div>
                        <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200">
                          <div className="grid grid-cols-[minmax(0,1.6fr)_100px_110px_120px] gap-3 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                            <span>{t('membersTitle', { defaultValue: 'Membro' })}</span>
                            <span>{t('financePaymentStatus', { defaultValue: 'Status' })}</span>
                            <span>{t('financeProviderLabel', { defaultValue: 'Canal' })}</span>
                            <span>{t('financeAmountLabel', { defaultValue: 'Valor' })}</span>
                          </div>
                          {payments.length === 0 ? (
                            <div className="px-4 py-6 text-sm text-slate-500">
                              {t('financeNoPayments', { defaultValue: 'Ainda não existem pagamentos nesta campanha.' })}
                            </div>
                          ) : null}
                          {payments.map((payment) => (
                            <div key={payment.id} className="grid grid-cols-[minmax(0,1.6fr)_100px_110px_120px] gap-3 border-t border-slate-100 px-4 py-3 text-sm text-slate-700">
                              <div>
                                <p className="font-medium text-slate-900">{payment.customerName || payment.customerEmail || payment.email || '---'}</p>
                                <p className="text-xs text-slate-500">{formatDate(payment.confirmedAt || payment.createdAt)}</p>
                              </div>
                              <span className="text-xs font-semibold uppercase text-slate-500">{payment.status || '---'}</span>
                              <span className="text-xs font-semibold uppercase text-slate-500">{payment.provider || '---'}</span>
                              <span className="font-semibold text-slate-900">{formatCurrency(payment.amount)}</span>
                            </div>
                          ))}

                          {hasMorePayments ? (
                            <div className="border-t border-slate-100 px-4 py-3">
                              <button
                                type="button"
                                onClick={handleLoadMorePayments}
                                disabled={isLoadingMorePayments}
                                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-70"
                              >
                                {isLoadingMorePayments ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                                {isLoadingMorePayments ? t('loadingMore') : t('financeLoadMorePayments', { defaultValue: 'Carregar mais pagamentos' })}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <aside className="grid gap-3">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs font-medium uppercase tracking-[0.16em] text-slate-400">{t('financeBalanceTitle', { defaultValue: 'Resumo financeiro' })}</p>
                        <div className="mt-3 grid gap-3">
                          <div>
                            <p className="text-xs text-slate-500">{t('financeRaisedTotal', { defaultValue: 'Total bruto' })}</p>
                            <p className="text-lg font-bold text-slate-900">{formatCurrency(checkoutSummary?.total)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">{t('financeAvailableTotal', { defaultValue: 'Disponível' })}</p>
                            <p className="text-lg font-bold text-emerald-700">{formatCurrency(checkoutSummary?.availableTotal)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">{t('financePendingTotal', { defaultValue: 'Pendente' })}</p>
                            <p className="text-lg font-bold text-amber-600">{formatCurrency(checkoutSummary?.pendingTotal)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">{t('financePaymentsCount', { defaultValue: 'Pagamentos' })}</p>
                            <p className="text-lg font-bold text-slate-900">{checkoutSummary?.totalPayments || 0}</p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <p className="text-sm font-semibold text-slate-900">{t('financeStatusBreakdown', { defaultValue: 'Por status' })}</p>
                        <div className="mt-3 grid gap-2">
                          {statusRows.length === 0 ? (
                            <p className="text-sm text-slate-500">{t('financeNoStatusData', { defaultValue: 'Sem pagamentos suficientes para distribuir por status.' })}</p>
                          ) : null}
                          {statusRows.map((row) => (
                            <div key={row.key} className="rounded-2xl bg-slate-50 px-3 py-2">
                              <div className="flex items-center justify-between text-xs font-semibold uppercase text-slate-500">
                                <span>{row.key}</span>
                                <span>{row.count}</span>
                              </div>
                              <p className="mt-1 text-sm font-semibold text-slate-900">{formatCurrency(row.total)}</p>
                              <progress
                                className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200 [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-slate-200 [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-emerald-500"
                                max={100}
                                value={row.percent}
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <p className="text-sm font-semibold text-slate-900">{t('financeProvidersBreakdown', { defaultValue: 'Por canal' })}</p>
                        <div className="mt-3 grid gap-2">
                          {providerRows.length === 0 ? (
                            <p className="text-sm text-slate-500">{t('financeNoProviderData', { defaultValue: 'Sem pagamentos suficientes para distribuir por canal.' })}</p>
                          ) : null}
                          {providerRows.map((row) => (
                            <div key={row.key} className="rounded-2xl bg-slate-50 px-3 py-2">
                              <div className="flex items-center justify-between text-xs font-semibold uppercase text-slate-500">
                                <span>{row.key}</span>
                                <span>{row.count}</span>
                              </div>
                              <p className="mt-1 text-sm font-semibold text-slate-900">{formatCurrency(row.total)}</p>
                              <progress
                                className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200 [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-slate-200 [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-sky-500"
                                max={100}
                                value={row.percent}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </aside>
                  </div>
                ) : null}
              </section>

              <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <Banknote className="h-4 w-4 text-emerald-700" />
                  {t('financeTopCampaignsTitle', { defaultValue: 'Campanhas com melhor resultado' })}
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {(analytics?.topCampaigns || []).slice(0, 6).map((campaign) => (
                    <div key={campaign.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm font-semibold text-slate-900">{campaign.name || t('financeUntitledCampaign', { defaultValue: 'Checkout sem título' })}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {t('financeCampaignPaymentsCount', {
                          defaultValue: '{{count}} pagamentos confirmados',
                          count: campaign.count,
                        })}
                      </p>
                      <p className="mt-3 text-lg font-bold text-emerald-700">{formatCurrency(campaign.totalAmount)}</p>
                    </div>
                  ))}

                  {!isLoadingAnalytics && (analytics?.topCampaigns || []).length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 md:col-span-2 xl:col-span-3">
                      {t('financeNoCampaigns', { defaultValue: 'Quando houver pagamentos confirmados, os checkouts com maior arrecadação aparecem aqui.' })}
                    </div>
                  ) : null}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}